import styles from './NavigationMobile.module.scss'
import { Button, Shape } from '@boltenergy-be/design-system'
import { useEffect, useState } from 'react'
import classNames from 'classnames'
import LanguageSwitcher from 'components/language-switcher/LanguageSwitcher'
import Link from 'components/link/Link'
import NextLink from 'next/link'
import LogoWithContainer from 'assets/LogoWithContainer'
import { useWindowSize } from 'usehooks-ts'
import { useTranslation } from 'next-i18next/pages'
import { routes } from 'utils/routes'
import { getNavigationItems } from 'components/page-header/utils'
import { useGetLanguage } from 'hooks/useGetLanguage'
import { PLENTY_URL, PORTAL_URL } from 'constants/constants'
import { NavigationMobileProps } from 'components/page-header/navigation-mobile/types'

const NavigationMobile = ({ onSimulationClick, ref, loading }: NavigationMobileProps) => {
  // i18n
  const { t } = useTranslation('header')
  const language = useGetLanguage()

  // Local state
  const [isToggled, setIsToggled] = useState<string | null>(null)

  // Window size
  const { width } = useWindowSize({ initializeWithValue: false })
  const isCheckingWindowSize = typeof width === 'undefined' // Necessary check to prevent hydration errors
  const isTablet = !!width && width < 1200 // Based on $widthDesktop

  // Close the dialog when the window size changes to a non-tablet size
  useEffect(() => {
    if (!isTablet && !isCheckingWindowSize && ref.current?.open) {
      ref.current.close()
    }
  }, [isTablet, isCheckingWindowSize])

  return (
    <dialog ref={ref} className={styles.dialog}>
      <header>
        <NextLink href={routes(language).home}>
          <LogoWithContainer className={styles.logo} />
        </NextLink>

        <Button type="button" leadingIcon="cross" className={styles.close} variant="tertiary" onClick={() => ref?.current?.close()} />
      </header>

      <Shape shape="s1" color="on-dark" className={styles.shape} />

      <div className={styles['small-bar']}>
        <LanguageSwitcher className={styles.languages} />

        <div className={styles.environments}>
          <span className={styles.active}>
            <strong>Bolt</strong>
          </span>
          /
          <Link href={`${PLENTY_URL}/${language}?simulation_so=Bolt%20Website`} as="a" target="_blank">
            Plenty
          </Link>
        </div>
      </div>

      <nav className={styles.navigation}>
        <ul className={styles['main-item-list']}>
          {getNavigationItems(language).map(({ key, route, children }) => (
            <li className={styles['main-item-wrapper']} key={key}>
              {route ? (
                <Link href={route} className={styles.item} onClick={() => ref?.current?.close()}>
                  {t(`navigation.${key}`)}
                </Link>
              ) : (
                <>
                  <Button
                    variant="tertiary"
                    trailingIcon="chevronDown"
                    className={styles.item}
                    onClick={() => setIsToggled(isToggled === key ? null : key)}
                  >
                    {t(`navigation.${key}.root`)}
                  </Button>
                  <ul className={styles.dropdown} data-open={isToggled === key}>
                    {children?.map(({ key, route }) =>
                      route ? (
                        <li key={key}>
                          <Link
                            className={classNames('h5 serif', styles['dropdown-item'])}
                            href={route}
                            onClick={() => ref?.current?.close()}
                          >
                            {t(`navigation.${key}`)}
                          </Link>
                        </li>
                      ) : null
                    )}
                  </ul>
                </>
              )}
            </li>
          ))}
        </ul>
      </nav>

      <Link className={classNames('mt-400', styles['my-bolt'])} href={PORTAL_URL} isFullwidth representation="button" variant="layered">
        {t('header:buttons.myBolt')}
      </Link>

      <Button isFullwidth variant="primary" onClick={onSimulationClick} {...{ loading }}>
        {t('common:calculateProposal')}
      </Button>
    </dialog>
  )
}

export default NavigationMobile
